import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

/**
 * The words a client might not know, explained the same way everywhere.
 * One place, so "lead credit" never means two things on two pages.
 */
export const GLOSSARY = {
  credit: 'Money you have added, counted at normal lead prices. Prepaid money buys more: $100 added at 20% off is $125 of credit.',
  tier: 'How strong the lead is, graded C, B, A or A+ on the conversation our rep had with the person. The price follows the tier.',
  bill: 'Leads that came after your credit ran out. They are charged at full price and billed on the 1st of the next month.',
  grading: 'The rep is still talking with them. The lead is graded on the whole conversation and charged then, within 48 hours.',
  minimum: 'The least added each month. Your monthly payment covers it automatically if you turn it on.',
  refill: 'When your credit gets low, add more from your card automatically, at the prepaid discount, so leads keep the discount.',
} as const

/**
 * A term with a small "?" that explains it. Opens on tap as well as hover,
 * because phones have no hover; closes on a second tap, a tap elsewhere, or Escape.
 */
export function Term({ children, tip }: { children: ReactNode; tip: keyof typeof GLOSSARY | string }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const ref = useRef<HTMLSpanElement>(null)
  const text = tip in GLOSSARY ? GLOSSARY[tip as keyof typeof GLOSSARY] : tip
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close) }
  }, [open])
  return (
    <span className="term" ref={ref}>
      {children}
      <button type="button" className="term-q" aria-label="What does this mean?" aria-expanded={open} aria-controls={id}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o) }}>?</button>
      {open && <span className="term-tip" role="note" id={id}>{text}</span>}
    </span>
  )
}
